import { DbDatabase, DbSphere, DbTheme, DbSubtheme, DbSemanticUnit, DbOffer, DbOfferBinding } from './types';
import { donskojSpheres1 } from '../data/donskojSpheres1';
import { donskojSpheres2 } from '../data/donskojSpheres2';
import { donskojSpheres3 } from '../data/donskojSpheres3';
import { LifeSphere } from '../types';
import { mockProducts } from '../ProductsPage';
import { mockAnglesData, mockAdsData, initialProductCabinetIds, allConnectedCabinets } from '../components/SemanticMapTab';
import { mockCampaigns, mockAdGroups, mockCampaignAds } from '../components/campaigns/CampaignsTab';

const now = () => new Date().toISOString();

const flattenNested = (nested: LifeSphere[]) => {
  const spheres: DbSphere[] = [];
  const themes: DbTheme[] = [];
  const subthemes: DbSubtheme[] = [];
  const semanticUnits: DbSemanticUnit[] = [];
  nested.forEach((s, si) => {
    spheres.push({
      id: s.id, productId: s.productId, name: s.name, description: s.description,
      status: s.status, priority: s.priority, history: s.history,
      displayOrder: si, createdAt: now(),
    });
    s.themes.forEach((t, ti) => {
      themes.push({
        id: t.id, sphereId: t.sphereId, name: t.name, description: t.description,
        keywords: t.keywords, images: t.images,
        status: t.status, priority: t.priority, history: t.history,
        displayOrder: ti, createdAt: now(),
      });
      t.subthemes.forEach((st, sti) => {
        subthemes.push({
          id: st.id, themeId: st.themeId, name: st.name, description: st.description,
          keywords: st.keywords,
          status: st.status, priority: st.priority, history: st.history,
          displayOrder: sti, createdAt: now(),
        });
        st.units.forEach((u, ui) => {
          semanticUnits.push({
            id: u.id, subthemeId: u.subthemeId, name: u.name, description: u.description,
            examples: u.examples, keywords: u.keywords,
            status: u.status, priority: u.priority, history: u.history,
            displayOrder: ui, createdAt: now(),
          });
        });
      });
    });
  });
  return { spheres, themes, subthemes, semanticUnits };
};

export const seedDonskoj = (db: DbDatabase): DbDatabase => {
  // Продукт уже может существовать в верхнем моке (ProductsPage). Добавляем запись если нет.
  // Seed продуктов из mockProducts (ProductsPage).
  db.products = [...mockProducts];

  // Seed общих таблиц (углы, объявления, кабинеты, привязки к продуктам).
  db.angles = [...mockAnglesData];
  db.ads = [...mockAdsData];
  db.adCabinets = [...allConnectedCabinets];
  db.productCabinetLinks = Object.entries(initialProductCabinetIds).flatMap(([productId, ids]) =>
    ids.map((cabinetId) => ({ productId, cabinetId })),
  );

  // Seed кампаний.
  db.campaigns = [...mockCampaigns];
  db.adGroups = [...mockAdGroups];
  db.campaignAds = [...mockCampaignAds];

  const nested = [...donskojSpheres1, ...donskojSpheres2, ...donskojSpheres3];
  const flat = flattenNested(nested);
  db.spheres.push(...flat.spheres);
  db.themes.push(...flat.themes);
  db.subthemes.push(...flat.subthemes);
  db.semanticUnits.push(...flat.semanticUnits);

  // Тестовые офферы с разными уровнями привязок.
  const productId = '10';
  const offers: DbOffer[] = [
    {
      id: 'of_webinar', productId, name: 'Бесплатный вебинар «Влияние мышления на человека»',
      description: 'Регистрация на прямой эфир + бонус-книга "Диагностика жизни"',
      keyPromise: 'Узнайте, как мышление влияет на здоровье, отношения и финансы',
      averageCheck: 0, desiredConversionCost: 150, metrikaConversionId: 'webinar_reg', createdAt: now(),
    },
    {
      id: 'of_course', productId, name: 'Курс «Энергия мысли. Открой себя»',
      description: '6 практических занятий от Анатолия Донского',
      keyPromise: 'Вы выйдете из замкнутого круга и поверите в себя', averageCheck: 35000,
      desiredConversionCost: 4500, metrikaConversionId: 'course_purchase', createdAt: now(),
    },
    {
      id: 'of_health', productId, name: 'Исцеление тела через мышление',
      description: 'Оффер с фокусом на проблемы со здоровьем',
      keyPromise: 'Избавьтесь от хронических болей через работу с сознанием',
      averageCheck: 35000, desiredConversionCost: 3500, createdAt: now(),
    },
    {
      id: 'of_emotions', productId, name: 'Работа с эмоциями и обидами',
      description: 'Оффер на эмоциональные проблемы', keyPromise: 'Отпустите обиды и верните внутренний покой',
      averageCheck: 35000, desiredConversionCost: 3500, createdAt: now(),
    },
    {
      id: 'of_money', productId, name: 'Мышление достатка',
      description: 'Оффер для темы финансов и денежного потолка',
      keyPromise: 'Пробейте финансовый потолок через смену убеждений',
      averageCheck: 35000, desiredConversionCost: 3500, createdAt: now(),
    },
  ];
  db.offers.push(...offers);

  const b = (offerId: string, scope: DbOfferBinding['scope'], scopeId: string): DbOfferBinding => ({
    id: `ob_${offerId}_${scope}_${scopeId}`, offerId, scope, scopeId, createdAt: now(),
  });

  db.offerBindings.push(
    b('of_webinar', 'product', productId),
    b('of_course', 'product', productId),
    b('of_health', 'sphere', 'ds1'),
    b('of_emotions', 'sphere', 'ds2'),
    b('of_money', 'sphere', 'ds4'),
  );

  return db;
};
